"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSchemaManager = void 0;
const react_1 = require("react");
const schemaExtractor_1 = require("../utils/schemaExtractor");
const useSchemaManager = (options) => {
    const { userId, onSchemaExtracted, onError } = options;
    const [currentSchema, setCurrentSchema] = (0, react_1.useState)(null);
    const [isExtracting, setIsExtracting] = (0, react_1.useState)(false);
    const [extractionErrors, setExtractionErrors] = (0, react_1.useState)([]);
    const [extractionWarnings, setExtractionWarnings] = (0, react_1.useState)([]);
    // Extract schema from HTML
    const extractSchema = (0, react_1.useCallback)((html, metadata) => {
        var _a;
        console.log("🔍 Starting schema extraction...");
        setIsExtracting(true);
        setExtractionErrors([]);
        setExtractionWarnings([]);
        try {
            const result = (0, schemaExtractor_1.extractSchemaFromHTML)(html, Object.assign(Object.assign({}, metadata), { userId }));
            if (result.success && result.schema) {
                console.log("✅ Schema extracted successfully:", result.schema);
                setCurrentSchema(result.schema);
                setExtractionWarnings(result.warnings || []);
                onSchemaExtracted === null || onSchemaExtracted === void 0 ? void 0 : onSchemaExtracted(result.schema);
            }
            else {
                console.error("❌ Schema extraction failed:", result.errors);
                setExtractionErrors(result.errors || ["Unknown extraction error"]);
                onError === null || onError === void 0 ? void 0 : onError(((_a = result.errors) === null || _a === void 0 ? void 0 : _a.join(", ")) || "Schema extraction failed");
            }
        }
        catch (error) {
            console.error("❌ Error in schema extraction:", error);
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            setExtractionErrors([errorMessage]);
            onError === null || onError === void 0 ? void 0 : onError(errorMessage);
        }
        finally {
            setIsExtracting(false);
        }
    }, [userId, onSchemaExtracted, onError]);
    // Update schema from new HTML
    const updateSchema = (0, react_1.useCallback)((newHtml) => {
        var _a;
        if (!currentSchema) {
            console.warn("⚠️ No existing schema to update");
            return;
        }
        console.log("🔄 Updating existing schema...");
        setIsExtracting(true);
        setExtractionErrors([]);
        setExtractionWarnings([]);
        try {
            const result = (0, schemaExtractor_1.updateSchemaFromHTML)(currentSchema, newHtml);
            if (result.success && result.schema) {
                console.log("✅ Schema updated successfully:", result.schema);
                setCurrentSchema(result.schema);
                setExtractionWarnings(result.warnings || []);
                onSchemaExtracted === null || onSchemaExtracted === void 0 ? void 0 : onSchemaExtracted(result.schema);
            }
            else {
                console.error("❌ Schema update failed:", result.errors);
                setExtractionErrors(result.errors || ["Unknown update error"]);
                onError === null || onError === void 0 ? void 0 : onError(((_a = result.errors) === null || _a === void 0 ? void 0 : _a.join(", ")) || "Schema update failed");
            }
        }
        catch (error) {
            console.error("❌ Error in schema update:", error);
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            setExtractionErrors([errorMessage]);
            onError === null || onError === void 0 ? void 0 : onError(errorMessage);
        }
        finally {
            setIsExtracting(false);
        }
    }, [currentSchema, onSchemaExtracted, onError]);
    // Clear schema
    const clearSchema = (0, react_1.useCallback)(() => {
        console.log("🗑️ Clearing schema");
        setCurrentSchema(null);
        setExtractionErrors([]);
        setExtractionWarnings([]);
    }, []);
    // Get input by ID
    const getInputById = (0, react_1.useCallback)((id) => {
        return currentSchema === null || currentSchema === void 0 ? void 0 : currentSchema.inputs.find((input) => input.id === id);
    }, [currentSchema]);
    // Get inputs by type
    const getInputsByType = (0, react_1.useCallback)((type) => {
        return (currentSchema === null || currentSchema === void 0 ? void 0 : currentSchema.inputs.filter((input) => input.type === type)) || [];
    }, [currentSchema]);
    // Get required inputs
    const getRequiredInputs = (0, react_1.useCallback)(() => {
        return (currentSchema === null || currentSchema === void 0 ? void 0 : currentSchema.inputs.filter((input) => input.isRequired)) || [];
    }, [currentSchema]);
    // Get validation rules for an input
    const getInputValidations = (0, react_1.useCallback)((inputId) => {
        const input = getInputById(inputId);
        return (input === null || input === void 0 ? void 0 : input.validations) || [];
    }, [getInputById]);
    return {
        // State
        currentSchema,
        isExtracting,
        extractionErrors,
        extractionWarnings,
        // Actions
        extractSchema,
        updateSchema,
        clearSchema,
        setCurrentSchema,
        // Getters
        getInputById,
        getInputsByType,
        getRequiredInputs,
        getInputValidations,
        // Computed values
        inputCount: (currentSchema === null || currentSchema === void 0 ? void 0 : currentSchema.inputs.length) || 0,
        requiredInputCount: (currentSchema === null || currentSchema === void 0 ? void 0 : currentSchema.inputs.filter((input) => input.isRequired).length) || 0,
        hasSchema: !!currentSchema,
    };
};
exports.useSchemaManager = useSchemaManager;
